import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { BreaksStudioGate } from "./breaksStudio/BreaksStudioGate";
import { RouteError } from "./components/RouteError";
import { parseEventLogSearch } from "./lib/analyticsEventLog";
import { parseInsightsSearch } from "./lib/analyticsInsights";
import { parseAuditLogSearch } from "./lib/auditLog";
import { parseBoxEditorSearch } from "./lib/boxEditorTabs";
import { parseChatSearch } from "./lib/chat";
import { parsePackingSearch } from "./lib/packing";
import { parsePaymentsSearch } from "./lib/paymentsTabs";
import { parseShopifySalesSearch } from "./lib/shopifySalesTabs";
import { parseStorefrontSearch } from "./lib/storefrontTabs";
import { setRouteTenant } from "./lib/tenantScope";
import { type AnalyticsWindow, isAnalyticsWindow } from "./lib/timeBuckets";
import { parseUserSearch } from "./lib/userTabs";
import { AcceptAdminInvitePage } from "./pages/AcceptAdminInvite";
import { AcceptInvitePage } from "./pages/AcceptInvite";
import { AnalyticsPage } from "./pages/Analytics";
import { AnalyticsEventsPage } from "./pages/AnalyticsEvents";
import { AnalyticsInsightsPage } from "./pages/AnalyticsInsights";
import { AuditLogPage } from "./pages/AuditLog";
import { BoxEditorPage } from "./pages/BoxEditor";
import { BoxesPage } from "./pages/Boxes";
import { BoxTemplatePage } from "./pages/BoxTemplate";
import { BoxTemplatesPage } from "./pages/BoxTemplates";
import { DomainsPage } from "./pages/Domains";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { CatalogItemPage } from "./pages/fulfillment/CatalogItem";
import { CatalogItemsPage } from "./pages/fulfillment/CatalogItems";
import { InventoryPage } from "./pages/fulfillment/Inventory";
import { ListingsPage } from "./pages/fulfillment/Listings";
import { OrderPage } from "./pages/fulfillment/Order";
import { OrdersPage } from "./pages/fulfillment/Orders";
import { PackingPage } from "./pages/fulfillment/Packing";
import { ProcurementPage } from "./pages/fulfillment/Procurement";
import { StockxFetchQueuePage } from "./pages/fulfillment/StockxFetchQueue";
import { StockxProductPage } from "./pages/fulfillment/StockxProduct";
import { StockxSearchPage } from "./pages/fulfillment/StockxSearch";
import { VendorProductSourcingPage } from "./pages/fulfillment/VendorProductSourcing";
import { GiftCodeBatchPage } from "./pages/GiftCodeBatch";
import { GiftCodesPage } from "./pages/GiftCodes";
import { HomePage } from "./pages/Home";
import { KeysPage } from "./pages/Keys";
import { AppLayout } from "./pages/Layout";
import { LoginPage } from "./pages/Login";
import { NotificationsMarketingPage } from "./pages/NotificationsMarketing";
import { NotificationsSystemPage } from "./pages/NotificationsSystem";
import { OpenerStylePage } from "./pages/OpenerStyle";
import { OpenersPage } from "./pages/Openers";
import { OverviewPage } from "./pages/Overview";
import { PaymentsPage } from "./pages/Payments";
import { PlatformAdminsPage } from "./pages/PlatformAdmins";
import { PlatformBattlesPage } from "./pages/PlatformBattles";
import { PlatformLegalPage } from "./pages/PlatformLegal";
import { PlatformOpenerStylePage } from "./pages/PlatformOpenerStyle";
import { PlatformOpenerStylesPage } from "./pages/PlatformOpenerStyles";
import { PlatformShippingPage } from "./pages/PlatformShipping";
import { PlatformShopifyInstallPage } from "./pages/PlatformShopifyInstall";
import { PlatformSoundsPage } from "./pages/PlatformSounds";
import { PlatformSwitchesPage } from "./pages/PlatformSwitches";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { RevenuePage } from "./pages/Revenue";
import { SandboxesPage } from "./pages/Sandboxes";
import { SettingsPage } from "./pages/Settings";
import { ShippingPage } from "./pages/Shipping";
import { ShopifyLayout } from "./pages/Shopify";
import { ShopifyAppearancePage } from "./pages/ShopifyAppearance";
import { ShopifyFeesPage } from "./pages/ShopifyFees";
import { ShopifyOverviewPage } from "./pages/ShopifyOverview";
import { ShopifySalesPage } from "./pages/ShopifySales";
import { StorefrontBattlesPage } from "./pages/StorefrontBattles";
import { StorefrontCategoriesPage } from "./pages/StorefrontCategories";
import { StorefrontChatPage } from "./pages/StorefrontChat";
import { StorefrontForgePage } from "./pages/StorefrontForge";
import { StorefrontGeneralPage } from "./pages/StorefrontGeneral";
import { StorefrontLayout } from "./pages/StorefrontLayout";
import { StorefrontLegalPage } from "./pages/StorefrontLegal";
import { StorefrontPagesPage } from "./pages/StorefrontPages";
import { StorefrontSocialLinksPage } from "./pages/StorefrontSocialLinks";
import { StorefrontSoundsPage } from "./pages/StorefrontSounds";
import { StorefrontSupportPage } from "./pages/StorefrontSupport";
import { TeamPage } from "./pages/Team";
import { TenantLayout } from "./pages/TenantLayout";
import { UserPage } from "./pages/User";
import { UsersPage } from "./pages/Users";

const rootRoute = createRootRoute({
  component: Outlet,
  // Old flat paths (/boxes, /keys, …) land here after the tenant-centric restructure.
  notFoundComponent: () => (
    <div className="flex min-h-svh items-center justify-center text-sm text-muted-foreground">
      <p>
        Page not found —{" "}
        <Link to="/" className="text-primary underline">
          back to tenants
        </Link>
        .
      </p>
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

/** Outside the app shell, which sends a visitor with no session to /login. */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

/** The page a reset email links to, outside the app shell like /forgot-password. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
});

/**
 * Tenant-invite landing page, outside the app shell — the visitor usually has no
 * session yet, and the shell would bounce them to /login.
 */
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  component: AcceptInvitePage,
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: typeof search.id === "string" && search.id ? search.id : undefined,
  }),
});

/**
 * Platform-admin invite landing page, outside the app shell for the same reason: the
 * invitee has no account until they set a password here.
 */
const acceptAdminInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-admin-invite",
  component: AcceptAdminInvitePage,
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: typeof search.id === "string" && search.id ? search.id : undefined,
  }),
});

/**
 * The Breaks Studio (`docs/breaks-studio.md`): full screen, outside the sidebar layout, and for
 * platform admins only (the gate checks both). Each page is a lazy chunk because it carries the
 * studio's own three.js build.
 */
const breaksStudioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/breaks-studio",
  component: BreaksStudioGate,
  beforeLoad: () => setRouteTenant(null),
});

const breaksStudioIndexRoute = createRoute({
  getParentRoute: () => breaksStudioRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./breaksStudio/StudioPage"), "StudioPage"),
});

const breaksStudioPanelsRoute = createRoute({
  getParentRoute: () => breaksStudioRoute,
  path: "panels",
  component: lazyRouteComponent(() => import("./breaksStudio/PanelsPage"), "PanelsPage"),
});

const breaksStudioDemoRoute = createRoute({
  getParentRoute: () => breaksStudioRoute,
  path: "demo",
  component: lazyRouteComponent(() => import("./breaksStudio/DemoPage"), "DemoPage"),
});

/** Everything below shares the sidebar layout and requires a session. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppLayout,
});

/**
 * Home branches by standing: the tenant table for platform admins, the member's
 * own tenant (or picker) otherwise. Leaving tenant scope clears the x-loot-tenant header.
 */
const tenantsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: HomePage,
  beforeLoad: () => setRouteTenant(null),
});

/**
 * Legacy bookmarks: tenants used to live under /orgs. The list moved home; a
 * tenant page keeps its path below the new prefix, query string included.
 */
const legacyOrgsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orgs",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
});

const legacyOrgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orgs/$tenantId/$",
  beforeLoad: ({ params, location }) => {
    const suffix = params._splat ? `/${params._splat}` : "";
    throw redirect({ href: `/tenants/${params.tenantId}${suffix}${location.searchStr}` });
  },
});

/**
 * The global Loot OS box templates every tenant can enable. Outside tenant
 * scope on purpose: a box template belongs to the platform, and editing one
 * changes it for every tenant that enabled it.
 *
 * beforeLoad clears the tenant scope the way tenantsListRoute does, and that is
 * load-bearing rather than tidy. Arriving from a tenant page would otherwise keep
 * sending that tenant's x-loot-tenant header, and — because query keys carry no tenant
 * dimension — leave its cache warm on a page with no tenant. platform.boxTemplates.*
 * ignores the header, but an env-scoped call made here would not fail: it would
 * resolve against session.activeOrganizationId. Declared on the parent so a
 * future /templates/… page cannot forget it; setRouteTenant early-returns on an
 * unchanged value, so list → editor does not re-clear the cache.
 */
const templatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/templates",
  component: Outlet,
  beforeLoad: () => setRouteTenant(null),
});

const templatesIndexRoute = createRoute({
  getParentRoute: () => templatesRoute,
  path: "/",
  component: BoxTemplatesPage,
});

const templateBoxRoute = createRoute({
  getParentRoute: () => templatesRoute,
  path: "$boxId",
  validateSearch: parseBoxEditorSearch,
  component: BoxTemplatePage,
});

/** Legacy bookmarks: box templates were "curated boxes" on /curated. */
const legacyCuratedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/curated",
  beforeLoad: () => {
    throw redirect({ to: "/templates" });
  },
});

const legacyCuratedBoxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/curated/$boxId",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/templates/$boxId", params });
  },
});

/** Platform-admin roster. Clears tenant scope the way /templates does, same reasons. */
const platformAdminsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/platform-admins",
  component: PlatformAdminsPage,
  beforeLoad: () => setRouteTenant(null),
});

/** The platform shipping default, platform admins only. Clears tenant scope like /templates. */
const platformShippingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping",
  component: PlatformShippingPage,
  beforeLoad: () => setRouteTenant(null),
});

/** The platform default for the battle loser prize's image, platform admins only. */
const platformBattlesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/battles",
  component: PlatformBattlesPage,
  beforeLoad: () => setRouteTenant(null),
});

/** The platform default sound per opener event, platform admins only. */
const platformSoundsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sounds",
  component: PlatformSoundsPage,
  beforeLoad: () => setRouteTenant(null),
});

/** Platform opener styles and the platform default, platform admins only. */
const platformOpenerStylesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/opener-styles",
  component: PlatformOpenerStylesPage,
  beforeLoad: () => setRouteTenant(null),
});

/** One platform opener style's editor; `new` creates one. */
const platformOpenerStyleNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/opener-styles/new",
  component: PlatformOpenerStylePage,
  beforeLoad: () => setRouteTenant(null),
});

const platformOpenerStyleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/opener-styles/$styleId",
  component: PlatformOpenerStylePage,
  beforeLoad: () => setRouteTenant(null),
});

/** The install link for this stage's Shopify app on one store, platform admins only. */
const platformShopifyInstallRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shopify-install",
  component: PlatformShopifyInstallPage,
  beforeLoad: () => setRouteTenant(null),
});

/** Platform-wide switches, platform admins only. Clears tenant scope like /templates. */
const platformSwitchesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/platform-switches",
  component: PlatformSwitchesPage,
  beforeLoad: () => setRouteTenant(null),
});

/**
 * Legal / Consent: the platform defaults for the legal identity the policy pages name, the
 * Termly website cookie banners run on and the banner's style, platform admins only.
 */
const platformLegalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/legal",
  component: PlatformLegalPage,
  beforeLoad: () => setRouteTenant(null),
});

/** Legacy bookmark: the Termly website and banner style are cards on Legal / Consent now. */
const platformCookieConsentRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cookie-consent",
  beforeLoad: () => {
    throw redirect({ to: "/legal" });
  },
});

/** The audit log, platform admins only; its filters live in the URL so a view can be shared. */
const auditLogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: AuditLogPage,
  beforeLoad: () => setRouteTenant(null),
  validateSearch: parseAuditLogSearch,
});

/**
 * All tenant-scoped UI. beforeLoad runs before children render, so the x-loot-tenant
 * header is set before any query fires. It is side-effectful — never enable
 * router preloading (defaultPreload) or hovering a link would switch scope.
 */
const tenantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tenants/$tenantId",
  component: TenantLayout,
  beforeLoad: ({ params }) => setRouteTenant(params.tenantId),
});

const tenantIndexRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/",
  component: OverviewPage,
});

const tenantBoxesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "boxes",
  component: BoxesPage,
});

const tenantBoxEditorRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "boxes/$boxId",
  validateSearch: parseBoxEditorSearch,
  component: BoxEditorPage,
});

/**
 * The Shopify area: one layout route (the linked shop, or the link card) with the overview at
 * its index and a child per section, the Shopify item's children in `TENANT_NAV`.
 */
const tenantShopifyRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "shopify",
  component: ShopifyLayout,
});

const tenantShopifyIndexRoute = createRoute({
  getParentRoute: () => tenantShopifyRoute,
  path: "/",
  component: ShopifyOverviewPage,
});

/** Purchases, orders and store credit, one page with a tab each (`?tab=`, `shopifySalesTabs.ts`). */
const tenantShopifySalesRoute = createRoute({
  getParentRoute: () => tenantShopifyRoute,
  path: "sales",
  component: ShopifySalesPage,
  validateSearch: parseShopifySalesSearch,
});

const tenantShopifyAppearanceRoute = createRoute({
  getParentRoute: () => tenantShopifyRoute,
  path: "appearance",
  component: ShopifyAppearancePage,
});

/** Loot Labs' fee on each settled unit, billed through Shopify (`shopify.usageCharges`). */
const tenantShopifyFeesRoute = createRoute({
  getParentRoute: () => tenantShopifyRoute,
  path: "fees",
  component: ShopifyFeesPage,
});

/** Legacy bookmark: the in-tenant catalog page, whose box templates are listed on Boxes now. */
const tenantCatalogRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "catalog",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/boxes", params });
  },
});

/**
 * The storefront area: one layout route (the config gate, the environment remount) with
 * General at its index and a child per section, the Storefront item's children in
 * `TENANT_NAV`. /branding and /rarity are legacy bookmarks from when every section was a
 * flat sibling of a hub page; /theme from when the theme was a section rather than a tab.
 * Domains sits here since it moved in from beside the hub (`/domains` redirects).
 */
const tenantStorefrontRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "storefront",
  component: StorefrontLayout,
});

const tenantStorefrontIndexRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "/",
  component: StorefrontGeneralPage,
  // ?tab picks the tab (general, theme, terminology), so a tab is a link.
  validateSearch: parseStorefrontSearch,
});

/** Legacy bookmark: the theme is a tab of the Storefront page now. */
const tenantStorefrontThemeRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "theme",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront",
      params,
      search: { tab: "theme" },
    });
  },
});

const tenantStorefrontSocialLinksRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "social-links",
  component: StorefrontSocialLinksPage,
});

const tenantStorefrontCategoriesRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "categories",
  component: StorefrontCategoriesPage,
});

const tenantStorefrontPagesRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "pages",
  component: StorefrontPagesPage,
});

/** Legacy bookmark: the Template page became Pages, which also holds the site frame. */
const tenantStorefrontTemplateRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "template",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/pages", params });
  },
});

/** Legacy bookmark: terminology is a tab of the Storefront page now. */
const tenantStorefrontTerminologyRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "terminology",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront",
      params,
      search: { tab: "terminology" },
    });
  },
});

/** Legacy bookmark: the currency is a tab of Storefront → Payments now. */
const tenantStorefrontCurrencyRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "currency",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront/payments",
      params,
      search: { tab: "currency" },
    });
  },
});

/** Legacy bookmark: the Features page is gone; every switch sits on the page it belongs to. */
const tenantStorefrontFeaturesRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "features",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront", params });
  },
});

/** Legacy bookmark: shipping is a page of its own beside the hub now. */
const tenantStorefrontShippingRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "shipping",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/shipping", params });
  },
});

/**
 * Payments is a storefront section: the switch, the packs and the purchases are the
 * environment's. The revenue report sits beneath it.
 */
const tenantStorefrontPaymentsRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "payments",
  component: PaymentsPage,
  // ?tab picks the tab (account, currency, packs, purchases, disputes), so a tab is a link.
  validateSearch: parsePaymentsSearch,
});

const tenantStorefrontPaymentsRevenueRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "payments/revenue",
  component: RevenuePage,
  // ?g picks the bucket granularity, ?bucket filters the invoice list to one period.
  validateSearch: (
    search: Record<string, unknown>,
  ): { g?: "day" | "month" | "year"; bucket?: string } => ({
    g: search.g === "day" || search.g === "month" || search.g === "year" ? search.g : undefined,
    bucket: typeof search.bucket === "string" && search.bucket ? search.bucket : undefined,
  }),
});

const tenantStorefrontBattlesRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "battles",
  component: StorefrontBattlesPage,
});

const tenantStorefrontSoundsRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "sounds",
  component: StorefrontSoundsPage,
});

/** Chat: the switch, then moderation (room, reports, mutes), a tab each (`?tab=`). */
const tenantStorefrontChatRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "chat",
  component: StorefrontChatPage,
  validateSearch: parseChatSearch,
});

/** Forge: the switch, the creator commission and the preset box images. */
const tenantStorefrontForgeRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "forge",
  component: StorefrontForgePage,
});

/**
 * Notifications: the system templates and the marketing sends, a page each with a tab to the
 * other, under the Storefront item's one Notifications row. The section's own path has no page
 * and opens the templates.
 */
const tenantStorefrontNotificationsRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "notifications",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/notifications/system", params });
  },
});

const tenantStorefrontNotificationsSystemRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "notifications/system",
  component: NotificationsSystemPage,
});

const tenantStorefrontNotificationsMarketingRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "notifications/marketing",
  component: NotificationsMarketingPage,
});

/** Gift codes: the environment's batches, and Generate Codes. */
const tenantStorefrontGiftCodesRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "gift-codes",
  component: GiftCodesPage,
});

/** One batch: its codes, the CSV download and the voids. */
const tenantStorefrontGiftCodeBatchRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "gift-codes/$batchId",
  component: GiftCodeBatchPage,
});

const tenantStorefrontSupportRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "support",
  component: StorefrontSupportPage,
});

const tenantStorefrontLegalRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "legal",
  component: StorefrontLegalPage,
});

/** Legacy bookmark: cookie consent is a row on the Legal / Consent page now. */
const tenantStorefrontCookieConsentRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "cookie-consent",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/legal", params });
  },
});

/** Legacy bookmark: branding is on General now. */
const tenantStorefrontBrandingRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "branding",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront", params });
  },
});

/** Legacy bookmark: rarity colors are a row on the Theme tab now. */
const tenantStorefrontRarityRedirectRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "rarity",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront",
      params,
      search: { tab: "theme" },
    });
  },
});

/**
 * Domains is a storefront section: the hostnames are the environment's, like the config they
 * serve. The DNS provider sends the tenant back here after one-step setup: ?connected names the
 * row, ?error / ?error_description come from a declined or failed apply (OAuth-style codes).
 */
const tenantStorefrontDomainsRoute = createRoute({
  getParentRoute: () => tenantStorefrontRoute,
  path: "domains",
  component: DomainsPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { connected?: string; error?: string; error_description?: string } => ({
    connected:
      typeof search.connected === "string" && search.connected ? search.connected : undefined,
    error: typeof search.error === "string" && search.error ? search.error : undefined,
    error_description:
      typeof search.error_description === "string" && search.error_description
        ? search.error_description
        : undefined,
  }),
});

/**
 * Legacy bookmark, and the return leg of a Domain Connect apply URL minted before Domains moved
 * under Storefront: the query string (?connected=… or ?error=…) rides along.
 */
const tenantDomainsRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "domains",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      href: `/tenants/${params.tenantId}/storefront/domains${location.searchStr}`,
    });
  },
});

/**
 * Legacy bookmark, and the return leg of a Stripe onboarding link minted before Payments moved
 * under Storefront: the query string (?onboarding=return&mode=…) rides along.
 */
const tenantPaymentsRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "payments",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      href: `/tenants/${params.tenantId}/storefront/payments${location.searchStr}`,
    });
  },
});

/** Chat moderation moved under Storefront → Chat; `?tab=` rides along. */
const tenantChatRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "chat",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      href: `/tenants/${params.tenantId}/storefront/chat${location.searchStr}`,
    });
  },
});

/** Where this environment ships and what it costs: a page of its own beside Storefront. */
/**
 * Openers: the environment's default opener style and its own styles. Top-level, not a
 * storefront section: a style reaches the Shopify overlay and the API too.
 */
const tenantOpenersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "openers",
  component: OpenersPage,
});

/** One of the environment's opener styles, a page of its own; `new` creates one. */
const tenantOpenerStyleNewRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "openers/new",
  component: OpenerStylePage,
});

const tenantOpenerStyleRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "openers/$styleId",
  component: OpenerStylePage,
});

const tenantShippingRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "shipping",
  component: ShippingPage,
});

const tenantAnalyticsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "analytics",
  component: AnalyticsPage,
  // ?w picks the window (24h/7d/30d/90d), which also decides hourly or daily buckets.
  validateSearch: (search: Record<string, unknown>): { w?: AnalyticsWindow } => ({
    w: isAnalyticsWindow(search.w) ? search.w : undefined,
  }),
});

const tenantAnalyticsInsightsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "analytics/insights",
  component: AnalyticsInsightsPage,
  // The whole insight (?w ?m ?c ?e: window, measure, chart, events), so an insight is a link.
  validateSearch: parseInsightsSearch,
});

const tenantAnalyticsEventsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "analytics/events",
  component: AnalyticsEventsPage,
  // The range (?w or ?day) and the filters, so a log view is a link.
  validateSearch: parseEventLogSearch,
});

/** Legacy bookmark: the revenue report moved under Storefront → Payments with its filters. */
const tenantPaymentsRevenueRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "payments/revenue",
  beforeLoad: ({ params, location }) => {
    throw redirect({
      href: `/tenants/${params.tenantId}/storefront/payments/revenue${location.searchStr}`,
    });
  },
});

/** Legacy bookmark: the packs are a tab of Storefront → Payments now. */
const tenantBalancePacksRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "balance-packs",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront/payments",
      params,
      search: { tab: "packs" },
    });
  },
});

/** Legacy bookmark: the packs page from before the unit was renamed to cents. */
const tenantLegacyPacksRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "gem-packs",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/tenants/$tenantId/storefront/payments",
      params,
      search: { tab: "packs" },
    });
  },
});

const tenantKeysRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "keys",
  component: KeysPage,
});

const tenantUsersRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "users",
  component: UsersPage,
  // ?user=<endUserId> opens that user's detail sheet (deep link from Payments).
  validateSearch: (search: Record<string, unknown>): { user?: string } => ({
    user: typeof search.user === "string" && search.user ? search.user : undefined,
  }),
});

/** The full customer page; the sheet on /users links here. */
const tenantUserRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "users/$userId",
  component: UserPage,
  // ?tab picks the history tab, so a tab is a link.
  validateSearch: parseUserSearch,
});

/** Legacy bookmark: Gift codes moved under Storefront. */
const tenantGiftCodesRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "gift-codes",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/gift-codes", params });
  },
});

/** Legacy bookmark: a batch moved with Gift codes. */
const tenantGiftCodeBatchRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "gift-codes/$batchId",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/gift-codes/$batchId", params });
  },
});

/** Legacy bookmarks: Notifications moved under Storefront, each page to its own new path. */
const tenantNotificationsRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "notifications",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/notifications/system", params });
  },
});

const tenantNotificationsSystemRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "notifications/system",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/notifications/system", params });
  },
});

const tenantNotificationsMarketingRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "notifications/marketing",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/tenants/$tenantId/storefront/notifications/marketing", params });
  },
});

const tenantSandboxesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "sandboxes",
  component: SandboxesPage,
});

const tenantSettingsRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "settings",
  component: SettingsPage,
});

const tenantTeamRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "team",
  component: TeamPage,
});

/**
 * Platform fulfillment console. Clears tenant scope the way /templates does: these
 * pages read platform.fulfillment.*, which ignores the tenant header entirely.
 * Declared on the parent so a future /fulfillment/… page cannot forget it.
 */
const fulfillmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment",
  component: Outlet,
  beforeLoad: () => setRouteTenant(null),
});

const listingsRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "listings",
  component: ListingsPage,
});

const catalogItemsRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "items",
  component: CatalogItemsPage,
});

const catalogItemRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "items/$itemId",
  component: CatalogItemPage,
});

const inventoryRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "inventory",
  component: InventoryPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "orders",
  component: Outlet,
  // TCG Order List and TCG Fulfill Order keep Retool's width, for the list and every order.
  staticData: { pageWidth: "retool" },
});

const ordersIndexRoute = createRoute({
  getParentRoute: () => ordersRoute,
  path: "/",
  component: OrdersPage,
});

const orderRoute = createRoute({
  getParentRoute: () => ordersRoute,
  path: "$orderId",
  component: OrderPage,
});

const packingRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "packing",
  component: PackingPage,
  validateSearch: parsePackingSearch,
});

const procurementRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "procurement",
  component: ProcurementPage,
});

const stockxRoute = createRoute({
  getParentRoute: () => fulfillmentRoute,
  path: "stockx",
  component: Outlet,
});

const stockxSearchRoute = createRoute({
  getParentRoute: () => stockxRoute,
  path: "/",
  component: StockxSearchPage,
  // ?q is the submitted query, ?page its 1-based page, so back/forward restores a search.
  validateSearch: (search: Record<string, unknown>): { q?: string; page?: number } => {
    const page = Number(search.page);
    return {
      q: typeof search.q === "string" && search.q ? search.q : undefined,
      page: Number.isInteger(page) && page > 0 ? page : undefined,
    };
  },
});

const stockxQueueRoute = createRoute({
  getParentRoute: () => stockxRoute,
  path: "queue",
  component: StockxFetchQueuePage,
});

const stockxSourcingRoute = createRoute({
  getParentRoute: () => stockxRoute,
  path: "sourcing",
  component: VendorProductSourcingPage,
});

const stockxProductRoute = createRoute({
  getParentRoute: () => stockxRoute,
  path: "$productId",
  component: StockxProductPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInviteRoute,
  acceptAdminInviteRoute,
  breaksStudioRoute.addChildren([
    breaksStudioIndexRoute,
    breaksStudioPanelsRoute,
    breaksStudioDemoRoute,
  ]),
  appRoute.addChildren([
    tenantsListRoute,
    legacyOrgsRoute,
    legacyOrgRoute,
    templatesRoute.addChildren([templatesIndexRoute, templateBoxRoute]),
    legacyCuratedRoute,
    legacyCuratedBoxRoute,
    platformAdminsRoute,
    platformShippingRoute,
    platformBattlesRoute,
    platformSoundsRoute,
    platformOpenerStylesRoute,
    platformOpenerStyleNewRoute,
    platformOpenerStyleRoute,
    platformLegalRoute,
    platformCookieConsentRedirectRoute,
    auditLogRoute,
    platformSwitchesRoute,
    platformShopifyInstallRoute,
    fulfillmentRoute.addChildren([
      listingsRoute,
      catalogItemsRoute,
      catalogItemRoute,
      inventoryRoute,
      ordersRoute.addChildren([ordersIndexRoute, orderRoute]),
      procurementRoute,
      packingRoute,
      stockxRoute.addChildren([
        stockxSearchRoute,
        stockxQueueRoute,
        stockxSourcingRoute,
        stockxProductRoute,
      ]),
    ]),
    tenantRoute.addChildren([
      tenantIndexRoute,
      tenantBoxesRoute,
      tenantBoxEditorRoute,
      tenantShopifyRoute.addChildren([
        tenantShopifyIndexRoute,
        tenantShopifySalesRoute,
        tenantShopifyAppearanceRoute,
        tenantShopifyFeesRoute,
      ]),
      tenantCatalogRedirectRoute,
      tenantStorefrontRoute.addChildren([
        tenantStorefrontIndexRoute,
        tenantStorefrontPagesRoute,
        tenantStorefrontTemplateRedirectRoute,
        tenantStorefrontSocialLinksRoute,
        tenantStorefrontCategoriesRoute,
        tenantStorefrontTerminologyRedirectRoute,
        tenantStorefrontPaymentsRoute,
        tenantStorefrontPaymentsRevenueRoute,
        tenantStorefrontBattlesRoute,
        tenantStorefrontSoundsRoute,
        tenantStorefrontChatRoute,
        tenantStorefrontForgeRoute,
        tenantStorefrontNotificationsRoute,
        tenantStorefrontNotificationsSystemRoute,
        tenantStorefrontNotificationsMarketingRoute,
        tenantStorefrontGiftCodesRoute,
        tenantStorefrontGiftCodeBatchRoute,
        tenantStorefrontSupportRoute,
        tenantStorefrontLegalRoute,
        tenantStorefrontDomainsRoute,
        tenantStorefrontBrandingRedirectRoute,
        tenantStorefrontThemeRedirectRoute,
        tenantStorefrontRarityRedirectRoute,
        tenantStorefrontShippingRedirectRoute,
        tenantStorefrontCurrencyRedirectRoute,
        tenantStorefrontFeaturesRedirectRoute,
        tenantStorefrontCookieConsentRedirectRoute,
      ]),
      tenantOpenersRoute,
      tenantOpenerStyleNewRoute,
      tenantOpenerStyleRoute,
      tenantShippingRoute,
      tenantChatRedirectRoute,
      tenantDomainsRedirectRoute,
      tenantPaymentsRedirectRoute,
      tenantPaymentsRevenueRedirectRoute,
      tenantBalancePacksRedirectRoute,
      tenantLegacyPacksRedirectRoute,
      tenantKeysRoute,
      tenantUsersRoute,
      tenantAnalyticsRoute,
      tenantAnalyticsInsightsRoute,
      tenantAnalyticsEventsRoute,
      tenantUserRoute,
      tenantGiftCodesRedirectRoute,
      tenantGiftCodeBatchRedirectRoute,
      tenantNotificationsRedirectRoute,
      tenantNotificationsSystemRedirectRoute,
      tenantNotificationsMarketingRedirectRoute,
      tenantSandboxesRoute,
      tenantSettingsRoute,
      tenantTeamRoute,
    ]),
  ]),
]);

// NOTE: never add defaultPreload here — tenantRoute.beforeLoad has side effects.
export const router = createRouter({ routeTree, defaultErrorComponent: RouteError });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    /**
     * `retool`: the page keeps its Retool app's width (`appMaxWidth`, 1560px) and a tighter
     * gutter, so its tables fit as they do there (AppShell in pages/Layout.tsx). Unset: the
     * dashboard's own column. Read from every match, so a route's children inherit it.
     */
    pageWidth?: "retool";
  }
}
